import { Link as RouterLink } from "react-router-dom";
import { alpha, Box, Grid, Typography } from "@mui/material";
import { isEmpty } from "lodash-es";
import { HubCard } from "~/components/hub-card";
import { Asset } from "~/pages/inventory/asset/types";
import { useAssetInsights } from "~/pages/inventory/hooks";
import { Space } from "~/lib/types";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { DoNotDisturbOnOutlined } from "@mui/icons-material";
import { CompactRiskScoreBox } from "~/components/CompactRiskScoreBox/CompactRiskScoreBox";
import { pluralize } from "~/lib/pluralize";

interface AssetInsightsProps {
  assetMrn: string;
  space: Space;
  scope: SpaceOrWorkspaceScope;
  insights: Asset["insights"];
  isCicd: boolean;
}

export function AssetInsights({
  assetMrn,
  insights,
  space,
  scope,
  isCicd,
}: AssetInsightsProps) {
  const { insightsData } = useAssetInsights({
    assetMrn,
    insights,
    space,
    scope,
    isCicd,
  });

  return (
    <HubCard
      title="ASSET SCORE BREAKDOWN"
      loading={false}
      sx={{ minHeight: "100%" }}
      PaperProps={{
        gap: 2,
      }}
    >
      <Grid container spacing={3} mt={0}>
        {isEmpty(insightsData) ? (
          <NoInsights />
        ) : (
          insightsData.map((insight) => {
            return (
              <Grid
                container
                key={insight.title}
                className="asset-insight"
                columnGap={2}
                sx={{ flexWrap: "nowrap" }}
                size={{
                  sm: "grow",
                  md: 12 / insightsData.length,
                }}
              >
                <Grid className="asset-insight-score" size="auto">
                  {insight.score && (
                    <CompactRiskScoreBox
                      riskValue={insight.score.riskValue}
                      riskRating={insight.score.riskRating}
                      inPreviewMode={insight.disabled}
                    />
                  )}
                  {!insight.score && (
                    <Box
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: theme.spacing(6),
                        height: theme.spacing(6),
                        p: 0.5,
                        borderRadius: 1,
                        background: alpha(theme.palette.text.disabled, 0.1),
                      })}
                    >
                      <Typography
                        sx={(theme) => ({
                          color: theme.palette.text.disabled,
                          fontSize: theme.spacing(4),
                          fontWeight: 700,
                        })}
                      >
                        –
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid container justifyContent="space-between" size="grow">
                  <Grid>
                    <Box>
                      <Typography
                        className="asset-insight-title"
                        {...(insight.to
                          ? {
                              component: RouterLink,
                              to: insight.to,
                            }
                          : {})}
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {insight.title}
                      </Typography>

                      <Box
                        sx={{ display: "flex", columnGap: 2, flexWrap: "wrap" }}
                      >
                        <Typography
                          {...(insight.to && {
                            component: RouterLink,
                            to: insight.to,
                          })}
                          color="text.secondary"
                          fontWeight={700}
                          className="asset-insight-label"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {insight.count}
                        </Typography>

                        <Typography
                          component={RouterLink}
                          to={`exceptions?spaceId=${space.id}`}
                          color="text.secondary"
                          className="asset-insight-exceptions-count"
                          sx={{
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            gap: 0.5,
                            alignItems: "center",
                          }}
                        >
                          <DoNotDisturbOnOutlined fontSize="inherit" />
                          {insight.exceptionsCount}{" "}
                          {pluralize("Exception", insight.exceptionsCount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            );
          })
        )}
      </Grid>
    </HubCard>
  );
}

const NoInsights = () => {
  return (
    <Grid
      sx={{
        display: "flex",
        placeContent: "center",
        p: 2.5,
      }}
      size={12}
    >
      <Typography color="text.secondary" variant="subtitle1">
        No insights available
      </Typography>
    </Grid>
  );
};
